/** Small DOM wiring for the markup the components render. Everything else is the app's framework. */

/** Wire every `[data-copy]` button under `root` to the clipboard. Returns an unwire function. */
export function wireCopy(root: ParentNode, clipboard: { writeText(text: string): Promise<void> } | undefined = typeof navigator === "undefined" ? undefined : navigator.clipboard): () => void {
  const onClick = (event: Event): void => {
    const target = (event.target as Element | null)?.closest<HTMLElement>("[data-copy]");
    if (!target || !clipboard) return;
    const text = target.dataset["copy"] ?? "";
    void clipboard.writeText(text).then(() => {
      const label = target.getAttribute("aria-label") ?? target.textContent ?? "";
      if (!target.getAttribute("aria-label")) target.textContent = "Copied";
      setTimeout(() => {
        if (!target.getAttribute("aria-label")) target.textContent = label;
      }, 2000);
    });
  };
  root.addEventListener("click", onClick);
  return () => root.removeEventListener("click", onClick);
}

/** Say something to a live region (see `liveRegions`). */
export function announce(doc: Pick<Document, "getElementById">, politeness: "polite" | "assertive", message: string): void {
  const el = doc.getElementById(politeness === "assertive" ? "live-assertive" : "live-polite");
  if (el) el.textContent = message;
}

/** Tabs: `[role=tab]` buttons switch `[data-tab-panel]` sections inside `root`. Arrow keys move between tabs. Returns an unbind. */
export function wireTabs(root: ParentNode): () => void {
  const onClick = (event: Event): void => {
    const tab = (event.target as Element | null)?.closest<HTMLElement>('[role="tab"]');
    if (!tab) return;
    const list = tab.closest('[role="tablist"]');
    if (!list) return;
    for (const t of list.querySelectorAll<HTMLElement>('[role="tab"]')) {
      const on = t === tab;
      t.setAttribute("aria-selected", String(on));
      t.tabIndex = on ? 0 : -1;
      const panel = root.querySelector<HTMLElement>(`[data-tab-panel="${t.dataset["tab"] ?? ""}"]`);
      if (panel) panel.hidden = !on;
    }
  };
  const onKey = (event: Event): void => {
    const e = event as KeyboardEvent;
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    const tab = (e.target as Element | null)?.closest<HTMLElement>('[role="tab"]');
    const tabs = tab ? [...(tab.closest('[role="tablist"]')?.querySelectorAll<HTMLElement>('[role="tab"]') ?? [])] : [];
    const at = tab ? tabs.indexOf(tab) : -1;
    if (at === -1) return;
    const next = tabs[(at + (e.key === "ArrowRight" ? 1 : tabs.length - 1)) % tabs.length];
    next?.focus();
    next?.click();
  };
  root.addEventListener("click", onClick);
  root.addEventListener("keydown", onKey);
  return () => {
    root.removeEventListener("click", onClick);
    root.removeEventListener("keydown", onKey);
  };
}
